import { useProposalStore } from "@/stores/risk/risk";
import { PolicyIncludes, Quote } from "@/stores/risk/types/types";
import { getFormattedLogoName } from "@/utils";

function getLogoPath(insurer: string) {
    return new URL(`../../assets/logos/${getFormattedLogoName(insurer)}.png`, import.meta.url).href;
}

export function useQuoteForDisplay(quote: Quote) {
    const proposalStore = useProposalStore();

    enum CoverType {
        BUILDINGS_CONTENTS = "Buildings and Contents",
        BUILDINGS = "Buildings Only",
        CONTENTS = "Contents Only",
        NCD = "NCD"
    }

    const hasNcd = () => {
        return quote.discounts?.some(discount => discount.description === "No Claims Discount");
    };

    const coverType = () => {
        if (
            (proposalStore.proposal.risk.cover_details.buildings_cover_amount ?? 0) > 0 &&
            (proposalStore.proposal.risk.cover_details.contents_cover_amount ?? 0) > 0
        ) {
            return CoverType.BUILDINGS_CONTENTS;
        }
        if (
            (proposalStore.proposal.risk.cover_details.buildings_cover_amount ?? 0) > 0 &&
            !proposalStore.proposal.risk.cover_details.contents_cover_amount
        ) {
            return CoverType.BUILDINGS;
        }
        if (
            !proposalStore.proposal.risk.cover_details.buildings_cover_amount &&
            (proposalStore.proposal.risk.cover_details.contents_cover_amount ?? 0) > 0
        ) {
            return CoverType.CONTENTS;
        }
    };

    const policyIncludes: Array<PolicyIncludes> | null = [
        {
            id: CoverType.BUILDINGS,
            text: "Accidental Damage Buildings",
            included:
                quote.additional_details?.buildings_accidental_damage_cover_included &&
                parseInt(quote.additional_details.buildings_sum_insured_covered) > 0
        },
        {
            id: CoverType.CONTENTS,
            text: "Accidental Damage Contents",
            included:
                quote.additional_details?.contents_accidental_damage_cover_included &&
                parseInt(quote.additional_details.contents_sum_insured_covered) > 0
        },
        quote.discounts && { id: CoverType.NCD, text: "No Claims Discount Applied", included: hasNcd() }
    ];

    const voluntaryExcess = Number(quote.additional_details?.voluntary_excess ?? 0);
    const compulsoryExcess = Number(quote.compulsory_excess ?? 0);

    return {
        ...quote,
        id: quote.id,
        broker: "Applied Ireland",
        brand_phone_number: "0818 22 22 22",
        insurerName: quote.insurer_name,
        premium: quote.premium,
        net_premium: quote.net_premium,
        coverLevel: coverType(),
        icons: [],
        logo: getLogoPath(quote.insurer_name ?? "").toString(),
        monthly_deposit: quote.monthly_deposit,
        monthly_payment: quote.monthly_payment,
        monthly_total_payable: quote.monthly_total_payable,
        monthly_installments: quote.monthly_installments,
        monthly_apr: quote.monthly_apr,
        monthly_interest: quote.monthly_interest,
        excess: {
            voluntary: voluntaryExcess,
            compulsory: compulsoryExcess
        },
        underwriter: quote.insurer_name,
        insurer_benefits: quote.insurer_benefits,
        insurer_documents: quote.insurer_documents,
        selectable_benefits: quote.selectable_benefits,
        scheme_name: quote.scheme_name,
        policy_includes: policyIncludes,
        ipid_file_name: quote.insurer_benefits?.ipid?.split("/").slice(-1)[0],
        discounts: quote.discounts,
    };
}
